import React, { useEffect, useState } from "react";
import {
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateString,
  validationLength,
  validationType,
  validationMessages,
} from "../../actions/validations";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

const SnCUsers = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [lastName, setLastName] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [lastNameErr, setLastNameErr] = useState("");
  const [firstNameErr, setFirstNameErr] = useState("");
  const [userNameErr, setUserNameErr] = useState("");
  const [roleErr, setRoleErr] = useState("");

  useEffect(() => {
    fieldValues && setItems(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateInput = (): boolean => {
    setLastNameErr("");
    setFirstNameErr("");
    setUserNameErr("");
    setRoleErr("");
    if (!lastName) {
      setLastNameErr("Last Name" + validationMessages.sncMandatorySuffix);
    }
    if (!firstName) {
      setFirstNameErr("First Name" + validationMessages.sncMandatorySuffix);
    }
    if (!userName) {
      setUserNameErr("User Name" + validationMessages.sncMandatorySuffix);
    }
    if (!role) {
      setRoleErr("Role" + validationMessages.sncMandatorySuffix);
    }
    if (!lastName || !firstName || !userName || !role) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(
        lastName,
        validationLength.originName,
        validationType.OTHER,
      )
    ) {
      setLastNameErr(validationMessages.originName);
      return false;
    }
    if (
      !validateString(
        firstName,
        validationLength.originName,
        validationType.OTHER,
      )
    ) {
      setFirstNameErr(validationMessages.originName);
      return false;
    }
    if (
      !validateString(
        userName,
        validationLength.originName,
        validationType.OTHER,
      )
    ) {
      setUserNameErr(validationMessages.originName);
      return false;
    }
    if (
      !validateString(role, validationLength.roleName, validationType.OTHER)
    ) {
      setRoleErr(validationMessages.roleName);
      return false;
    }
    return true;
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (checkDuplicate(otherItems, "User Name", userName)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev: any) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SnCPropertyEnum.users} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(items ?? [], "User Name", userName)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev: any) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setLastNameErr("");
      setFirstNameErr("");
      setUserNameErr("");
      setRoleErr("");
      setLastName(list["Last Name"] ?? null);
      setFirstName(list["First Name"] ?? null);
      setUserName(list["User Name"] ?? null);
      setRole(list?.Role ?? null);
    } else {
      setLastName(null);
      setFirstName(null);
      setUserName(null);
      setRole(null);
      setLastNameErr("");
      setFirstNameErr("");
      setUserNameErr("");
      setRoleErr("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  const payload = {
    "Last Name": lastName,
    "First Name": firstName,
    "User Name": userName,
    Role: role,
  };

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Users
            <Tooltip
              title={tooltipComponent(
                golfTooltipHeaders?.categoryGroup ?? "Users",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px">
          <Stack direction="column">
            <FormLabel component="legend">
              Last Name*
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              value={lastName ?? ""}
              error={!!lastNameErr}
              helperText={lastNameErr}
              onChange={(e) => {
                setLastName(e.target.value || null);
                setLastNameErr("");
              }}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel component="legend">
              First Name*
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              value={firstName ?? ""}
              error={!!firstNameErr}
              helperText={firstNameErr}
              onChange={(e) => {
                setFirstName(e.target.value || null);
                setFirstNameErr("");
              }}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel component="legend">
              User Name
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              value={userName ?? ""}
              error={!!userNameErr}
              helperText={userNameErr}
              onChange={(e) => {
                setUserName(e.target.value || null);
                setUserNameErr("");
              }}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel component="legend">
              Role*
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              placeholder="Values from Role Setup"
              value={role ?? ""}
              error={!!roleErr}
              helperText={roleErr}
              onChange={(e) => {
                setRole(e.target.value || null);
                setRoleErr("");
              }}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} User
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Users Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code="User Name"
              name="Last Name"
            />
          ) : (
            <Typography variant="body2">No Users Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SnCUsers;
